import { redactServerMessage, SubscriptionInactiveError, UserFacingError } from '@/shared/domain'
import { HttpError } from './http-client'

export type ServerErrorMappings = Record<string, new (message?: string) => UserFacingError>

export type ServerFieldError = { field: string; message: string }

const SUBSCRIPTION_INACTIVE_MESSAGE = 'organization subscription is not active'

export function mapServerError(err: unknown, mappings: ServerErrorMappings = {}): never {
  const status = readServerErrorStatus(err)
  if (status === null) {
    throw err
  }

  const message = readServerErrorMessage(err)
  const normalizedMessage = message.toLowerCase()
  const isClientError = status >= 400 && status < 500
  const safeMessage = isClientError
    ? (redactServerMessage(readServerErrorBodyMessage(err)) ?? undefined)
    : undefined

  if (status === 403 && normalizedMessage.includes(SUBSCRIPTION_INACTIVE_MESSAGE)) {
    throw new SubscriptionInactiveError(message)
  }

  for (const key of Object.keys(mappings)) {
    if (!key.includes(':')) continue
    const [keyStatus, keyReason] = key.split(':', 2)
    if (Number(keyStatus) !== status) continue
    if (matchesReason(normalizedMessage, keyReason)) {
      const ErrorClass = mappings[key]
      throw new ErrorClass(safeMessage)
    }
  }

  const statusKey = String(status)
  const ErrorClass = mappings[statusKey]
  if (ErrorClass) {
    throw new ErrorClass(safeMessage)
  }

  if (status === 400 && safeMessage) {
    throw new UserFacingError(safeMessage)
  }

  throw err
}

export function readServerErrorStatus(err: unknown): number | null {
  if (err instanceof HttpError) return err.status
  if (err !== null && typeof err === 'object' && 'status' in err) {
    const status = (err as { status: unknown }).status
    if (typeof status === 'number') return status
  }
  return null
}

export function readServerErrorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    const bodyMessage = readBodyMessage(err.body)
    if (bodyMessage) return bodyMessage
    return err.message
  }
  if (err instanceof Error) return err.message
  return ''
}

export function readServerErrorBodyMessage(err: unknown): string | null {
  if (!(err instanceof HttpError)) return null
  return readBodyMessage(err.body)
}

export function readServerErrorCode(err: unknown): string | null {
  if (!(err instanceof HttpError)) return null
  const body = err.body
  if (body === null || typeof body !== 'object') return null
  const code = (body as { code?: unknown }).code
  if (typeof code === 'string') return code
  const data = (body as { data?: unknown }).data
  if (data === null || typeof data !== 'object') return null
  const dataCode = (data as { code?: unknown }).code
  return typeof dataCode === 'string' ? dataCode : null
}

export function readServerErrorData(err: unknown): unknown {
  if (!(err instanceof HttpError)) return undefined
  const body = err.body
  if (body === null || typeof body !== 'object') return undefined
  return (body as { data?: unknown }).data
}

export function readServerFieldErrors(err: unknown): ServerFieldError[] {
  if (!(err instanceof HttpError)) return []
  const body = err.body
  if (body === null || typeof body !== 'object') return []
  const errors = (body as { errors?: unknown }).errors
  if (!Array.isArray(errors)) return []
  const fieldErrors: ServerFieldError[] = []
  for (const entry of errors) {
    if (entry === null || typeof entry !== 'object') continue
    const { field, message } = entry as { field?: unknown; message?: unknown }
    if (typeof message !== 'string' || message.trim().length === 0) continue
    fieldErrors.push({ field: typeof field === 'string' ? field : '', message })
  }
  return fieldErrors
}

function readBodyMessage(body: unknown): string | null {
  if (body === null || typeof body !== 'object') return null
  const error = (body as { error?: unknown }).error
  if (typeof error === 'string') return error
  return null
}

function matchesReason(normalizedMessage: string, reason: string): boolean {
  const normalizedReason = reason.toLowerCase().replace(/_/g, ' ').trim()
  if (!normalizedReason) return false
  return normalizedMessage.includes(normalizedReason)
}
